import React, { useState } from "react";
import {
  Box,
  TextField,
  Popover,
  Typography,
  IconButton,
} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import CloseIcon from "@mui/icons-material/Close";
import { format } from "date-fns";
import styles from "./dateRangePicker.module.scss";

interface DateRangePickerProps {
  label: string;
  startDate: string;
  endDate: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  error?: boolean;
  helperText?: string;
  minStartDate?: string;
  minEndDate?: string;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  label,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  error = false,
  helperText,
  minStartDate,
  minEndDate,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    onStartDateChange("");
    onEndDateChange("");
  };

  const handleStartDateChange = (date: string) => {
    // If new start date is greater than end date, clear end date
    if (date && endDate && date > endDate) {
      onEndDateChange("");
    }
    onStartDateChange(date);
  };

  const open = Boolean(anchorEl);

  // Format display value
  const getDisplayValue = () => {
    if (!startDate && !endDate) return "";

    if (startDate && endDate) {
      try {
        const start = format(new Date(startDate), "MMM dd, yyyy");
        const end = format(new Date(endDate), "MMM dd, yyyy");
        return `${start} - ${end}`;
      } catch {
        return `${startDate} - ${endDate}`;
      }
    }

    if (startDate) {
      try {
        return format(new Date(startDate), "MMM dd, yyyy") + " - ...";
      } catch {
        return startDate + " - ...";
      }
    }

    return "";
  };

  const hasValue = startDate || endDate;

  // Calculate effective min date for end date input (max of startDate and minEndDate)
  const getEffectiveMinEndDate = (): string | undefined => {
    if (startDate && minEndDate) {
      return startDate > minEndDate ? startDate : minEndDate;
    }
    return minEndDate || startDate || undefined;
  };

  return (
    <>
      <Box className={styles.dateRangeTrigger}>
      <TextField
        label={label}
        value={getDisplayValue()}
        onClick={handleClick}
        fullWidth
        variant="outlined"
        size="medium"
        error={error}
        helperText={helperText}
        InputProps={{
          readOnly: true,
          startAdornment: (
            <CalendarTodayIcon
              sx={{ mr: 1, color: "action.active", fontSize: 20 }}
            />
          ),
          endAdornment: hasValue ? (
            <IconButton
              size="small"
              onClick={handleClear}
              sx={{ mr: -0.5 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          ) : null,
        }}
        sx={{
          cursor: "pointer",
          "& .MuiInputBase-root": {
            cursor: "pointer",
          },
          "& .MuiInputBase-input": {
            cursor: "pointer",
          },
        }}
      />
      </Box>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            mt: 1,
            p: 2,
            minWidth: 320,
          },
        }}
      >
        <Box className={styles.popoverContent}>
          <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
            Select Date Range
          </Typography>
          <Box className={styles.dateInputs}>
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => handleStartDateChange(e.target.value)}
              variant="outlined"
              size="small"
              fullWidth
              inputProps={{
                min: minStartDate || undefined,
                max: endDate || undefined,
              }}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{ mb: 1.5 }}
            />
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => onEndDateChange(e.target.value)}
              variant="outlined"
              size="small"
              fullWidth
              inputProps={{
                min: getEffectiveMinEndDate(),
              }}
              InputLabelProps={{
                shrink: true,
              }}
              disabled={!startDate}
            />
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default DateRangePicker;
